import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

export default function AdminJobSources() {
  const [boards, setBoards] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [filter, setFilter] = useState("live");

  const load = () => adminApi.get("/admin/job-sources")
    .then(r => { setBoards(r.data.boards || []); setCatalog(r.data.catalog || []); setSummary(r.data.summary); })
    .catch(() => toast.error("Could not load job sources"));
  useEffect(() => { load(); }, []);

  const update = (index, values) => setBoards(b => b.map((v, i) => i === index ? { ...v, ...values } : v));

  const save = async () => {
    setSaving(true);
    try { await adminApi.put("/admin/job-sources", { boards: boards.map(({ source, slug, company }) => ({ source, slug, company })) }); toast.success("Extra boards saved. Run Verify to connect them."); await load(); }
    catch { toast.error("Enter a company name and valid board slug for each source."); }
    finally { setSaving(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await adminApi.post("/admin/job-sources/verify");
      toast.success(`${data.live.length} live sources of ${data.checked} checked. ${data.disabled.length} auto-disabled.`);
      await load();
    } catch { toast.error("Verification could not finish. Please retry."); }
    finally { setVerifying(false); }
  };

  const toggle = async (row, active) => {
    try { await adminApi.put(`/admin/job-sources/${encodeURIComponent(row.key)}/active`, null, { params: { active } }); await load(); }
    catch { toast.error("Could not change that source"); }
  };

  const rows = catalog.filter(r => filter === "all" || (filter === "live" ? r.last_success && r.active !== false : !r.last_success || r.active === false));

  return <div className="space-y-4" data-testid="admin-job-sources">
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Job source health</h2>
        {summary && <p className="text-sm text-slate-500 mt-1" data-testid="job-sources-summary">{summary.live} live of {summary.total} configured · {summary.listings.toLocaleString()} cached vacancies. Dead boards are disabled automatically so members never see a broken feed.</p>}
      </div>
      <Button onClick={verify} disabled={verifying} className="bg-sky-600 hover:bg-sky-500 text-white shrink-0" data-testid="verify-job-sources">
        {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying all sources…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Verify all sources</>}
      </Button>
    </div>

    <div className="flex flex-wrap gap-2" data-testid="job-source-filters">
      {[["live", "Live"], ["dead", "Not connected"], ["all", "All"]].map(([id, label]) => (
        <button key={id} onClick={() => setFilter(id)} className={`rounded-full border px-3 py-1.5 text-xs ${filter === id ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-500"}`} data-testid={`job-source-filter-${id}`}>{label}</button>
      ))}
    </div>

    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500"><th className="p-3">Source</th><th className="p-3">Provider</th><th className="p-3">Vacancies</th><th className="p-3">Status</th><th className="p-3" /></tr></thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.key} className="border-t border-slate-200 text-slate-700" data-testid={`job-source-${row.key}`}>
              <td className="p-3 break-words">{row.company || row.slug}</td>
              <td className="p-3 capitalize text-slate-500">{row.source}</td>
              <td className="p-3">{row.count || 0}</td>
              <td className="p-3 text-xs">{row.active === false ? <span className="text-slate-500">Disabled</span> : row.last_success ? <span className="text-emerald-700">Connected</span> : <span className="text-amber-700">{row.error || "Not checked"}</span>}</td>
              <td className="p-3"><button onClick={() => toggle(row, row.active === false)} className="text-xs text-sky-700" data-testid={`job-source-toggle-${row.key}`}>{row.active === false ? "Enable" : "Disable"}</button></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="p-4 text-slate-500">Nothing in this view yet. Run "Verify all sources".</td></tr>}
        </tbody>
      </table>
    </div>

    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-3">
      <div>
        <h3 className="font-bold text-slate-900">Add your own boards</h3>
        <p className="text-sm text-slate-500 mt-1">Use the employer's official board token — Greenhouse, Lever, Ashby, Workable, SmartRecruiters or Recruitee. No arbitrary-site scraping. Boards that don't publish an API (Naukri, Indeed, LinkedIn, Monster) are offered to members as one-tap searches on their own sites instead.</p>
      </div>
      {boards.map((b, i) => <div key={i} className="rounded-lg bg-slate-50 border border-slate-200 p-3 grid sm:grid-cols-4 gap-3" data-testid={`job-source-row-${i}`}>
        <label className="text-xs text-slate-500">Company<Input value={b.company} onChange={e => update(i, { company: e.target.value })} maxLength={100} className="mt-1 bg-slate-50 border-slate-300" data-testid={`job-source-company-${i}`} /></label>
        <label className="text-xs text-slate-500">Provider<select value={b.source} onChange={e => update(i, { source: e.target.value })} className="block w-full mt-1 p-2 rounded-md bg-slate-50 border border-slate-300 text-sm" data-testid={`job-source-provider-${i}`}>
          {["greenhouse", "lever", "ashby", "workable", "smartrecruiters", "recruitee"].map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
        </select></label>
        <label className="text-xs text-slate-500">Board slug<Input value={b.slug} onChange={e => update(i, { slug: e.target.value })} maxLength={80} className="mt-1 bg-slate-50 border-slate-300" data-testid={`job-source-slug-${i}`} /></label>
        <div className="flex items-end"><Button variant="outline" size="sm" onClick={() => setBoards(v => v.filter((_, n) => n !== i))} className="border-slate-300 text-rose-600" data-testid={`job-source-remove-${i}`}>Remove</Button></div>
      </div>)}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" disabled={boards.length >= 60} onClick={() => setBoards(v => [...v, { source: "greenhouse", slug: "", company: "" }])} className="border-slate-300 text-slate-700" data-testid="add-job-source">Add company board</Button>
        <Button onClick={save} disabled={saving} className="bg-sky-600 text-white" data-testid="save-job-sources">{saving ? "Saving…" : "Save extra boards"}</Button>
      </div>
    </div>
  </div>;
}
